import { useState } from 'react'
import { CheckOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons'
import { Card, Tag } from 'antd'
import classnames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'

import {
    TOP,
    BOTTOM,
    FRONT,
    BACK,
    LEFT,
    RIGHT,
    ANNOTATION_MODE,
    CROSS_SECTION_ANNOTATION_MODE,
    CROSS_SECTION_MODE,
    SELECT_MODE,
} from '~/redux-modules/qmr-viewer/constants'
import { CrossSectionViewType } from '~/redux-modules/qmr-viewer/types'
import {
    getCrossSectionConfig,
    getForceHideCrossSectionTools,
    getPaletteColors,
    getSelectedPaletteColor,
    getToolVisibility,
    getViewerInteractionMode,
} from '~/redux-modules/qmr-viewer/selectors'
import {
    setCrossSectionView,
    setInteractionModeColor,
    setViewerInteractionMode,
    toggleHideCrossSectionTools,
} from '~/redux-modules/qmr-viewer/actions'
import Box from '~/core/Box'

import styles from './styles.css'

const SIDE = 'side'

type PlaneType = typeof FRONT | typeof TOP | typeof SIDE

const ColorPalette = () => {
    const [expanded, setExpanded] = useState(false)
    const dispatch = useDispatch()
    const paletteColors = useSelector(getPaletteColors)
    const selectedPaletteColor = useSelector(getSelectedPaletteColor)

    const Color = ({ color }: { color: string }) => {
        const selected = color === selectedPaletteColor

        const classNames = classnames({
            [styles.swatch]: true,
            [styles.selected]: selected,
        })

        return (
            <li
                onClick={() => dispatch(setInteractionModeColor(color))}
                style={{ backgroundColor: color }}
                className={classNames}
            >
                {selected && <CheckOutlined />}
            </li>
        )
    }

    const onIconClick = () => setExpanded(!expanded)

    return (
        <Box alignItems="center">
            <ul className={styles.palette}>
                {paletteColors
                    .map(color =>
                        expanded || color === selectedPaletteColor ? (
                            <Color key={color} color={color} />
                        ) : null,
                    )
                    .filter(color => color)}
            </ul>
            {expanded ? (
                <LeftOutlined
                    className={styles.paletteIcon}
                    onClick={onIconClick}
                />
            ) : (
                <RightOutlined
                    className={styles.paletteIcon}
                    onClick={onIconClick}
                />
            )}
        </Box>
    )
}

const PlaneButton = (props: { plane: PlaneType }) => {
    const { plane } = props

    const dispatch = useDispatch()
    const crossSectionConfig = useSelector(getCrossSectionConfig)

    let nextPlane: CrossSectionViewType = FRONT

    switch (plane) {
        case FRONT:
            nextPlane =
                (crossSectionConfig.view || FRONT) === FRONT ? BACK : FRONT // Start off with "Back"
            break
        case TOP:
            nextPlane = crossSectionConfig.view === TOP ? BOTTOM : TOP
            break
        case SIDE:
            nextPlane = crossSectionConfig.view === RIGHT ? LEFT : RIGHT
            break
    }

    const onClick = () => {
        return dispatch(setCrossSectionView(nextPlane))
    }

    return (
        <Tag onClick={onClick} className={styles.planeTag}>
            {nextPlane}
        </Tag>
    )
}

const HideToolsButton = () => {
    const dispatch = useDispatch()
    const hide = useSelector(getForceHideCrossSectionTools)

    return (
        <Tag
            onClick={() => dispatch(toggleHideCrossSectionTools())}
            className={styles.planeTag}
        >
            {hide ? 'enable' : 'disable'}
            {' tools'}
        </Tag>
    )
}

const ClearCrossSectionButton = () => {
    const dispatch = useDispatch()

    return (
        <Tag
            onClick={() =>
                dispatch(
                    setViewerInteractionMode({
                        mode: ANNOTATION_MODE,
                        annotatable: true,
                        selectAllDfmByDefault: false,
                    }),
                )
            }
            className={styles.clearCrossSectionTag}
        >
            clear cross section
        </Tag>
    )
}

const InteractionControlBox = () => {
    let inner

    const interactionMode = useSelector(getViewerInteractionMode)
    const toolVisibility = useSelector(getToolVisibility)

    switch (interactionMode) {
        case SELECT_MODE: {
            inner = <ColorPalette />
            break
        }
        case CROSS_SECTION_MODE:
            inner = (
                <>
                    <PlaneButton plane={FRONT} />
                    <PlaneButton plane={TOP} />
                    <PlaneButton plane={SIDE} />
                    <HideToolsButton />
                </>
            )
            break
        case CROSS_SECTION_ANNOTATION_MODE:
            inner = <ClearCrossSectionButton />
            break
        default:
            inner = null
    }

    if (inner == null) {
        return null
    }

    const classNames = classnames({
        [styles.controlsPanel]: true,
        [styles.interactionCard]: interactionMode === SELECT_MODE,
        [styles.interactionCardTagMode]: interactionMode !== SELECT_MODE,
    })

    if (!toolVisibility?.interactionControlBox) {
        return null
    }

    return (
        <Card className={classNames} bodyStyle={{ padding: 0 }}>
            {inner}
        </Card>
    )
}

export default InteractionControlBox
